import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SuccessCard } from 'common/components/SuccessCard';
import { DEFAULT_VALUE_0 } from 'common/config';
import { UserHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getRequestIdForView, getRequestStatus, getRequestSuppliesByStatus } from 'entities/Request/Request.helper';
import { IRequest } from 'entities/Request/Request.models';
import { ERequestStatus } from 'entities/Request/Request.enum';
import { getRequestList, selectIsRequestLoading } from 'entities/Request/Request.slice';

interface IComponentProps {
  isViewMode: boolean;
  request: IRequest;
  changeFooterOffsetHeightPx: (value: number) => void;
}

export const RequestFooter: FC<IComponentProps> = ({ isViewMode, request, changeFooterOffsetHeightPx }) => {
  const dispatch = useAppDispatch();

  const isRequestLoading = useAppSelector(selectIsRequestLoading);

  const [openSuccessCard, setOpenSuccessCard] = useState<boolean>(false);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { isManager } = UserHooks.useCurrentUserRole();

  const { isCreatedRequest } = getRequestStatus(request.status);
  const { pendingSupplies, cancelledSupplies } = useMemo(() => getRequestSuppliesByStatus(request), [request]);

  useEffect(() => {
    setOpenSuccessCard(false);
  }, [request.id]);

  useEffect(() => {
    if (!isCreatedRequest) {
      changeFooterOffsetHeightPx(DEFAULT_VALUE_0);
    }
  }, [isCreatedRequest]);

  useEffect(() => {
    if (footerRef.current) {
      changeFooterOffsetHeightPx(footerRef.current.offsetHeight);
    }
  }, [footerRef]);

  if (!(!isViewMode && isManager && isCreatedRequest)) {
    return null;
  }

  return (
    <>
      <div ref={footerRef} className="request-footer">
        <div>
          <div className="text-controls-accent mb-2">
            {`Вы согласовали ${pendingSupplies.length} из ${request.supplies.length - cancelledSupplies.length} товаров`}
          </div>

          <span className="text-tag color-dark-grey">
            Заказ будет автоматически передан поставщику, когда все позиции в заказе будут обработаны (согласованы или отклонены).
          </span>
        </div>
      </div>

      <SuccessCard
        open={openSuccessCard}
        subtitle="Готово!"
        content={`Заказ ${getRequestIdForView(request.id, request.createdAt)} согласован и будет размещен у поставщика.`}
        btnTitle="К следующему заказу"
        loading={isRequestLoading}
        onClose={() => setOpenSuccessCard(false)}
        onConfirm={async () => {
          const requestList = await dispatch(
            getRequestList({
              statuses: [ERequestStatus.Created],
              limit: 1,
            }),
          );
          const nextRequest = requestList?.[0];

          if (nextRequest) {
            navigate(`/request/${nextRequest.id}`);
          } else {
            navigate(`/request-list/manager`);
          }
        }}
      />
    </>
  );
};
